import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {GRAY_LIGHT, GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import {assertNever} from 'util/Types';

export function ChatMessageToolbar({
  left,
  right,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
}): JSX.Element {
  return (
    <div className={css(styles.messageToolbar)}>
      {left}
      {right}
    </div>
  );
}

export const ChatMessageBubble = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    toolbar: React.ReactNode;
    faded?: boolean;
    variant: 'report' | 'reply' | 'user_message';
  }
>(({children, variant, toolbar, faded = false}, ref): JSX.Element => {
  const body = (
    <>
      {children}
      {toolbar}
    </>
  );
  switch (variant) {
    case 'report':
      return (
        <div
          ref={ref}
          className={css(styles.systemReportLayout)}
          {...getTestProps({component: 'ChatMessageBubble', element: 'assistant-message-bubble'})}
        >
          <div
            className={css(styles.systemReportContainer)}
            {...getTestProps({component: 'ChatMessageBubble', element: 'system-report-message'})}
          >
            {body}
          </div>
        </div>
      );
    case 'reply':
      return (
        <div
          ref={ref}
          className={css(styles.assistantMessageLayout)}
          {...getTestProps({component: 'ChatMessageBubble', element: 'assistant-message-bubble'})}
        >
          <div className={css(styles.assistantMessageContainer)}>
            <div className={css(styles.assistantMessageContent)}>{body}</div>
          </div>
        </div>
      );
    case 'user_message':
      return (
        <div
          ref={ref}
          className={css(styles.userMessageLayout)}
          {...getTestProps({component: 'ChatMessageBubble', element: 'user-message-bubble'})}
        >
          <div
            className={css(styles.userMessageContainer, faded && styles.userMessageContainerFaded)}
            {...getTestProps({
              component: 'ChatMessageBubble',
              element: faded ? 'user-message-bubble-faded' : 'user-message-bubble-solid',
            })}
          >
            {body}
          </div>
        </div>
      );
    default:
      throw new Error(`Unknown variant: ${assertNever(variant)}`);
  }
});

const styles = StyleSheet.create({
  assistantMessageLayout: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  assistantMessageContainer: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
    padding: '12px',
    maxWidth: '80%',
  },
  assistantMessageContent: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },
  userMessageLayout: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userMessageContainer: {
    padding: '12px',
    maxWidth: 'min(400px, 80%)',
    backgroundColor: GRAY_LIGHTER,
    whiteSpace: 'pre-wrap',
    borderRadius: '4px',
    overflowWrap: 'break-word',
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  userMessageContainerFaded: {
    opacity: 0.5,
  },
  systemReportLayout: {
    display: 'flex',
    flexDirection: 'column',
  },
  systemReportContainer: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
    padding: '12px',
    ...FONT_PROXIMA_14,
  },
  messageToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
  },
});
